import { AIChatModeConfig } from '@/hooks/useAIChatModeConfig';
import { ChatTheme, Question, TriggerRule } from './theme';
import { FieldDictionary } from './fieldsDictionary.ts';
import { AIChatbotSettings } from "@/types/ai.ts";
import {v4 as uuidv4} from "uuid";

// Unified settings schema combining all builder settings
export interface UnifiedChatbotSettings {
  // General Settings
  chatMode: 'question_flow' | 'ai_only';
  messageDelay: number;
  buttonOptionsDelay: number;
  noResponseDelay: number;
  optionsHandlerDelay: number;
  showTypingIndicator: boolean;
  
  // AI Settings (consolidated from ai_settings)
  ai_chat_config: AIChatModeConfig;
  ai_provider: {
    provider: 'openai' | 'anthropic' | 'gemini';
    apiKey: string;
    model: string;
    temperature: number;
    maxTokens: number;
  };
  
  // Validation Settings
  validation_settings: {
    enabled: boolean;
    maxAttempts: number;
    strictValidation: boolean;
    followUpStyle?: string;
  };
  
  // Company Information
  companyName: string;
  companyLogo: string;
  tcpaLanguage: string;
  
  // Operator Settings
  operatorName: string;
  operatorAvatarUrl: string;

  ai_settings: AIChatbotSettings
}

// Default settings for new chatbots
export const DEFAULT_UNIFIED_SETTINGS: UnifiedChatbotSettings = {
  chatMode: 'ai_only',
  messageDelay: 1000,
  buttonOptionsDelay: 0,
  noResponseDelay: 2000,
  optionsHandlerDelay: 10,
  showTypingIndicator: true,
  
  ai_chat_config: {
    aiPersonality: {
      name: 'AI Assistant',
      role: 'Customer Service Representative',
      description: 'A helpful and knowledgeable AI assistant focused on providing excellent customer service and support.'
    },
    conversationStyle: {
      tone: 'professional',
      responseLength: 'adaptive',
      proactiveness: 'balanced'
    },
    systemInstructions: `You are a helpful AI assistant representing this company. Your goal is to:

1. Provide accurate and helpful information
2. Maintain a professional and friendly tone
3. Ask clarifying questions when needed
4. Guide users toward solutions
5. Escalate to human support when appropriate

Always be respectful, patient, and focused on helping the user achieve their goals.`,
    conversationGuidelines: [
      'Always greet users warmly and professionally',
      'Ask clarifying questions to better understand user needs',
      'Keep responses conversational and under 150 characters when possible',
      'Use short, friendly messages that feel natural and engaging'
    ],
    prohibitedTopics: [
      'Personal financial advice',
      'Medical or health advice',
      'Legal advice',
      'Political opinions'
    ],
    welcomeMessage: 'Hello! I\'m here to help you with any questions or concerns you may have. How can I assist you today?',
    fallbackMessage: 'I apologize, but I didn\'t quite understand that. Could you please rephrase your question or provide more details?',
    endConversationTriggers: ['goodbye', 'bye', 'end chat', 'no thanks', 'that\'s all'],
    
    // Goals System Configuration
    offerGoals: {
      enabled: false,
      goals: [
        {
          id: `${uuidv4()}`,
          name: 'Base Offer',
          customInstructions: '',
          actions: [
            {
              id: `${uuidv4()}`,
              type: 'fields_set',
              name: 'Fields Set',
              description: 'Click to expand and set needed fields',
              priority: 0,
              requiredFields: [
                {id: '7b585610-a80e-4489-beae-554c84511f18'},
                {id: '0e8d9efd-cd01-40b0-b7ff-1cd5c0dab113'},
                {id: '3f0307e3-bef3-4896-93ac-d5dd32bcf593'},
                {id: 'beb17824-f21c-4fd9-8353-c1439fd634ec'},
              ],
            },
            {
              id: `${uuidv4()}`,
              type: 'button',
              name: 'URL Button',
              description: 'Click to expand and set parameters',
              priority: 1,
            },
          ],
        }
      ]
    },
    
    contextMemory: true,
    maxConversationLength: 50,
    escalationTriggers: ['speak to human', 'human agent', 'manager', 'supervisor', 'complaint']
  },
  
  ai_provider: {
    provider: 'openai',
    apiKey: '',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    maxTokens: 1000
  },
  
  validation_settings: {
    enabled: false,
    maxAttempts: 3,
    strictValidation: false,
    followUpStyle: 'friendly'
  },
  
  companyName: '',
  companyLogo: '',
  tcpaLanguage: 'By providing your phone number, you consent to receive text messages from our company. Message and data rates may apply. Reply STOP to opt out at any time.',
  
  operatorName: '',
  operatorAvatarUrl: '',
  ai_settings: {
    messageStyle: 'traditional',
    typingSpeed: 400,
    messageDelay: 500,
    showTypingIndicator: true,
    streamingForAIOnly: true,
    aiServices: {
      primary: {
        provider: 'openai',
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2048
      }
    }
  }
};

// Combined chatbot data interface
export interface UnifiedChatbotData {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  title: string;
  avatar_url: string | null;
  operator_name: string;
  operator_avatar_url: string | null;
  company_name: string;
  company_logo: string | null;
  custom_javascript: string;
  custom_html: string;
  welcome_message: string;
  completion_message: string;
  active: boolean | null;
  created_at: string;
  updated_at: string;
  settings: UnifiedChatbotSettings;
  theme: ChatTheme | null;
  questions: Question[];
  triggerRules: TriggerRule[];
}

// Builder state interface
export interface BuilderState {
  // Data
  chatbot: UnifiedChatbotData | null;

  fieldsDictionary: FieldDictionary[] | [];

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  
  // Error states
  error: string | null;
  
  // Dirty state tracking
  isDirty: boolean;
  dirtyFields: Set<string>;
  
  // UI state
  activeTab: string;
  unsavedChanges: boolean;
  
  // Pending file uploads (stored in context for save operation)
  pendingOperatorAvatarFile: File | null;
  removeOperatorAvatarOnSave: boolean;
  pendingCompanyLogoFile: File | null;
  removeCompanyLogoOnSave: boolean;
}

// Builder action types
export type BuilderAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_PENDING_OPERATOR_AVATAR'; payload: File | null }
  | { type: 'SET_REMOVE_OPERATOR_AVATAR'; payload: boolean }
  | { type: 'SET_PENDING_COMPANY_LOGO'; payload: File | null }
  | { type: 'SET_REMOVE_COMPANY_LOGO'; payload: boolean }
  | { type: 'CLEAR_PENDING_FILES' }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CHATBOT'; payload: UnifiedChatbotData }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<UnifiedChatbotSettings> }
  | { type: 'UPDATE_THEME'; payload: Partial<ChatTheme> }
  | { type: 'UPDATE_QUESTIONS'; payload: Question[] }
  | { type: 'UPDATE_TRIGGER_RULES'; payload: TriggerRule[] }
  | { type: 'UPDATE_BASIC_INFO'; payload: { name?: string; description?: string; title?: string; welcome_message?: string; completion_message?: string } }
  | { type: 'SET_DIRTY'; payload: { field: string; isDirty: boolean } }
  | { type: 'RESET_DIRTY' }
  | { type: 'SET_ACTIVE_TAB'; payload: string }
  | { type: 'SET_FIELDS_DICTIONARY'; payload: FieldDictionary[] };
